import {
   GenericSearchResultItem,
   ProductSearchResultItem,
   NewsSearchResultItem,
   DeviceSearchResultItem,
   GuideSearchResultItem,
   SearchResultItem,
} from '../../types';
import { usePriceTier } from '@ifixit/app/hooks';
import { formatMoney, parseMoney } from '@ifixit/helpers';
import { type LocaleCode } from '@ifixit/i18n';
import { Highlight } from '../highlight';
import { useTranslations } from '@ifixit/i18n';
import { Doctype } from '../../types/indices';

/**
 * Formats a timestamp into a localized date string
 */
function formatTimestampToDate(timestamp: number): string {
   if (!timestamp) return '';
   const date = new Date(timestamp * 1000);
   return date.toLocaleDateString();
}

/**
 * News result component
 */
function NewsResult({ hit }: { hit: NewsSearchResultItem }) {
   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch">
         <div className="self-stretch text-gray-900 font-medium text-sm">
            <Highlight attribute="post_title" hit={hit} />
         </div>
         {hit.post_date && (
            <div className="text-gray-600 text-xs">
               {formatTimestampToDate(hit.post_date as number)}
            </div>
         )}
         {hit.content && (
            <div className="text-gray-800 text-sm mt-1">
               <Highlight attribute="content" hit={hit} isSnippet={true} />
            </div>
         )}
      </div>
   );
}

/**
 * Determines if a guide should be marked as featured
 */
function isGuideFeatured(hit: GuideSearchResultItem): boolean {
   return !!(hit.flags && hit.flags.includes('GUIDE_STARRED'));
}

/**
 * Guide result component
 */
function GuideResult({ hit }: { hit: GuideSearchResultItem }) {
   const isFeatured = isGuideFeatured(hit);
   const t = useTranslations('Search.cards');

   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch">
         <div className="flex justify-between w-full items-start">
            <div className="text-gray-900 font-medium text-sm">
               <Highlight attribute="title" hit={hit} />
            </div>
            {isFeatured && (
               <div className="bg-brand-700 text-white text-xs font-bold uppercase px-2 py-0.5 rounded flex-shrink-0 self-start">
                  {t('featured')}
               </div>
            )}
         </div>
         {hit.content && (
            <div className="text-gray-800 text-sm mt-1">
               <Highlight attribute="content" hit={hit} isSnippet={true} />
            </div>
         )}
      </div>
   );
}

/**
 * Formats a price range, handling both single prices and min/max ranges
 * Intelligently handles edge cases like zero minimum prices
 */
function formatPriceRange(
   minPrice: number,
   maxPrice: number,
   currency: string,
   localeCode: LocaleCode
): string {
   // If prices are the same, show single price
   if (minPrice === maxPrice) {
      return formatMoney(parseMoney(minPrice, currency), localeCode);
   }

   // If minimum is 0, likely show just the max price (or "up to" format)
   // This handles cases where min=0 doesn't represent a real $0.00 price
   if (minPrice === 0) {
      return formatMoney(parseMoney(maxPrice, currency), localeCode);
   }

   // Show full range for meaningful price ranges
   const formattedMinPrice = formatMoney(parseMoney(minPrice, currency), localeCode);
   const formattedMaxPrice = formatMoney(parseMoney(maxPrice, currency), localeCode);
   return `${formattedMinPrice} - ${formattedMaxPrice}`;
}

/**
 * Converts cents to decimal format (e.g., 395 cents -> 3.95)
 */
function convertCentsToDecimal(cents: number): number {
   return cents / 100;
}

/**
 * Determines the best available price display for a product
 */
function determineProductPrice(
   hit: ProductSearchResultItem,
   priceTier: string | null,
   currency: string,
   localeCode: LocaleCode
): string {
   // Priority 1: Use tier-specific pricing if available
   if (shouldUseTierPricing(hit, priceTier)) {
      return formatTierPricing(hit.price_tiers![priceTier!], currency, localeCode);
   }

   // Priority 2: Use group pricing (convert from cents)
   if (hasGroupPricing(hit)) {
      return formatGroupPricing(hit, currency, localeCode);
   }

   // Priority 3: Use float pricing
   if (hit.price_float !== undefined) {
      return formatMoney(parseMoney(hit.price_float, currency), localeCode);
   }

   // Fallback: Use original price string
   return hit.price || '';
}

function shouldUseTierPricing(hit: ProductSearchResultItem, priceTier: string | null): boolean {
   return !!(hit.price_tiers && priceTier && hit.price_tiers[priceTier]);
}

function hasGroupPricing(hit: ProductSearchResultItem): boolean {
   return hit.group_min_price !== undefined && hit.group_max_price !== undefined;
}

function formatTierPricing(
   tierPricing: { min: number; max: number },
   currency: string,
   localeCode: LocaleCode
): string {
   return formatPriceRange(tierPricing.min, tierPricing.max, currency, localeCode);
}

function formatGroupPricing(
   hit: ProductSearchResultItem,
   currency: string,
   localeCode: LocaleCode
): string {
   const minPriceInDecimal = convertCentsToDecimal(hit.group_min_price!);
   const maxPriceInDecimal = convertCentsToDecimal(hit.group_max_price!);
   return formatPriceRange(minPriceInDecimal, maxPriceInDecimal, currency, localeCode);
}

/**
 * Product result component showing price info
 */
function ProductResult({
   hit,
   currency,
   localeCode,
   storeCode,
}: {
   hit: ProductSearchResultItem;
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
}) {
   const { priceTier } = usePriceTier({ storeCode });
   const priceDisplay = determineProductPrice(hit, priceTier, currency, localeCode);

   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch">
         <div className="self-stretch text-gray-900 font-medium text-sm">
            <Highlight attribute="title" hit={hit} />
         </div>
         {priceDisplay && <div className="text-gray-800 text-sm">{priceDisplay}</div>}
      </div>
   );
}

/**
 * Device result component
 */
function DeviceResult({ hit }: { hit: DeviceSearchResultItem }) {
   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch text-gray-900 font-medium text-sm">
         <Highlight attribute="title" hit={hit} />
      </div>
   );
}

/**
 * Generic result content component
 */
function GenericResult({ hit }: { hit: GenericSearchResultItem }) {
   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch">
         <div className="self-stretch text-gray-900 font-medium text-sm">
            <Highlight attribute="title" hit={hit} />
         </div>
         {hit.description && (
            <div className="text-gray-800 text-sm">
               <Highlight attribute="description" hit={hit} isSnippet={true} />
            </div>
         )}
      </div>
   );
}

/**
 * Renders the appropriate result component based on doctype
 */
export function ResultContent({
   hit,
   currency,
   localeCode,
   storeCode,
}: {
   hit: SearchResultItem;
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
}) {
   switch (hit.doctype as Doctype) {
      case 'product': {
         return (
            <ProductResult
               hit={hit as ProductSearchResultItem}
               currency={currency}
               localeCode={localeCode}
               storeCode={storeCode}
            />
         );
      }
      case 'topic': {
         return <DeviceResult hit={hit as DeviceSearchResultItem} />;
      }
      case 'news': {
         return <NewsResult hit={hit as NewsSearchResultItem} />;
      }
      case 'guide': {
         return <GuideResult hit={hit as GuideSearchResultItem} />;
      }
      default: {
         return <GenericResult hit={hit as GenericSearchResultItem} />;
      }
   }
}
